// @ts-check
// Authentication utilities

import { resolveDisplayName } from './userDisplay.js';
import {
  EXEC_DASHBOARD_MODULE,
  executiveDashboardAccess,
} from '../../azure-functions/shared/executiveAccess.cjs';

/**
 * The four built-in tenant roles.
 * @typedef {'admin' | 'manager' | 'reader' | 'prospect'} TenantRole
 *
 * Access granted to one sidebar module.
 * @typedef {Object} ModuleAccess
 * @property {boolean} access
 * @property {'full' | 'readonly'} mode
 *
 * @typedef {Record<string, ModuleAccess>} ModulePermissions
 *
 * A tenant user record as stored in `tenant_users`.
 * @typedef {Object} TenantUser
 * @property {string} id
 * @property {string} [username]
 * @property {string} [displayName]
 * @property {TenantRole} [role]
 * @property {string} [passwordHash]
 * @property {string} [createdAt]
 * @property {string} [seasuiteRoleId]
 *
 * A configurable role from `state.seasuiteRoles`.
 * @typedef {Object} SeasuiteRole
 * @property {string} id
 * @property {string} [name]
 * @property {Record<string, boolean>} [modules]
 *
 * The signed-in user's session.
 * @typedef {Object} Session
 * @property {string} [id]
 * @property {TenantRole} [role]
 * @property {{ modules?: ModulePermissions }} [permissions]
 */

/** @type {Record<TenantRole, string>} */
const ROLES = {
  admin: 'Administrator',
  manager: 'Compliance Manager',
  reader: 'Reader',
  prospect: 'Prospect',
};

/**
 * Resolve user ID → display name.
 * Returns an em dash when no id was supplied and "Unknown user" when the id
 * matches nobody — the internal id is never rendered (see userDisplay.js).
 *
 * @param {string | null | undefined} userId
 * @param {TenantUser[] | null | undefined} users
 * @param {Record<string, any>[] | null} [employees] optional HR records, for
 *   ids that name an employee rather than a login
 * @returns {string}
 */
function resolveUser(userId, users, employees) {
  return resolveDisplayName(userId, { users, employees });
}

// Legacy SHA-256 helper — retained ONLY for seedUsers() (dev/localStorage fallback).
// Production auth flows must use pbkdf2Hash / verifyPassword below.
/**
 * @param {string} str
 * @returns {Promise<string>} lowercase hex digest
 */
async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const PBKDF2_ITERATIONS = 100_000;
/**
 * @param {ArrayBuffer | Uint8Array} buf
 * @returns {string}
 */
const toHex = (buf) =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
/**
 * @param {string} hex - must have even length; malformed input yields an empty array
 * @returns {Uint8Array}
 */
const fromHex = (hex) => new Uint8Array((hex.match(/.{2}/g) || []).map((b) => parseInt(b, 16)));

/**
 * Hash a password with PBKDF2-SHA-256 (100 000 iterations, 256-bit output).
 * Returns "pbkdf2:{salt_hex}:{hash_hex}" — same format as the Azure Functions.
 *
 * @param {string} password
 * @returns {Promise<string>}
 */
async function pbkdf2Hash(password) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const hashBuffer = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    keyMaterial,
    256
  );
  return `pbkdf2:${toHex(salt)}:${toHex(hashBuffer)}`;
}

/**
 * Verify a password against a stored hash.
 * Supports both PBKDF2 ("pbkdf2:salt:hash") and legacy SHA-256 (64-char hex).
 *
 * @param {string} password
 * @param {string} storedHash
 * @returns {Promise<boolean>} false for any unrecognised hash format
 */
async function verifyPassword(password, storedHash) {
  if (storedHash.startsWith('pbkdf2:')) {
    const parts = storedHash.split(':');
    if (parts.length !== 3) return false;
    const salt = fromHex(parts[1]);
    const expected = parts[2];
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    const hashBuffer = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
      keyMaterial,
      256
    );
    return toHex(hashBuffer) === expected;
  }
  if (/^[0-9a-f]{64}$/.test(storedHash)) {
    return (await sha256(password)) === storedHash;
  }
  return false;
}

/**
 * Dev/localStorage fallback only — never used by the production Azure auth path.
 * @returns {Promise<TenantUser[]>}
 */
async function seedUsers() {
  try {
    const raw = localStorage.getItem('seasuite_users');
    if (raw) return JSON.parse(raw);
    const hash = await sha256('SeaSuite');
    /** @type {TenantUser[]} */
    const users = [
      {
        id: `user-${Math.random().toString(36).substr(2, 9)}`,
        username: 'Administrator',
        displayName: 'Administrator',
        role: 'admin',
        passwordHash: hash,
        createdAt: new Date().toISOString(),
      },
    ];
    localStorage.setItem('seasuite_users', JSON.stringify(users));
    return users;
  } catch {
    return [];
  }
}

/** @returns {TenantUser[]} */
function loadUsers() {
  try {
    return JSON.parse(localStorage.getItem('seasuite_users') || '[]');
  } catch {
    return [];
  }
}
/**
 * @param {TenantUser[]} users
 * @returns {void}
 */
function saveUsers(users) {
  try {
    localStorage.setItem('seasuite_users', JSON.stringify(users));
  } catch {}
}

/**
 * @param {TenantRole | string | null | undefined} role
 * @returns {boolean}
 */
function canEdit(role) {
  return role === 'admin' || role === 'manager';
}
/**
 * @param {TenantRole | string | null | undefined} role
 * @returns {boolean}
 */
function canAdmin(role) {
  return role === 'admin';
}
/**
 * @param {TenantRole | string | null | undefined} _role
 * @returns {boolean}
 */
function canViewGap(_role) {
  return true;
} // all roles can view gap analyses
/**
 * @param {TenantRole | string | null | undefined} role
 * @returns {boolean}
 */
function isProspect(role) {
  return role === 'prospect';
} // prospects only see gap analyses

export {
  ROLES,
  resolveUser,
  pbkdf2Hash,
  verifyPassword,
  seedUsers,
  loadUsers,
  saveUsers,
  canEdit,
  canAdmin,
  canViewGap,
  isProspect,
};

// ── Sidebar modules — matches navConfig.js groups ────────────────────────────
// Each entry maps to a nav item key. `group` is used by the Module Roles editor
// to organise toggles visually; it has no runtime effect on filtering.
/** @type {Array<{ key: string, label: string, group: string }>} */
const SIDEBAR_MODULES = [
  // Work
  { key: 'dashboard', label: 'Dashboard', group: 'Work' },
  { key: 'tasks', label: 'Tasks', group: 'Work' },
  { key: 'calendar', label: 'Calendar', group: 'Work' },
  { key: 'nextsteps', label: 'Next Steps', group: 'Work' },
  { key: 'guides', label: 'Guides', group: 'Work' },
  // Governance
  { key: 'controls', label: 'Controls', group: 'Governance' },
  { key: 'documents', label: 'Documents', group: 'Governance' },
  { key: 'soa', label: 'Applicability', group: 'Governance' },
  { key: 'management-review', label: 'Mgt Review', group: 'Governance' },
  // Risk
  { key: 'risks', label: 'Risk Register', group: 'Risk' },
  { key: 'threats', label: 'Threats', group: 'Risk' },
  { key: 'vulnerabilities', label: 'Vulnerabilities', group: 'Risk' },
  { key: 'priority', label: 'Priority', group: 'Risk' },
  { key: 'ai-governance', label: 'AI Governance', group: 'Risk' },
  // Privacy
  { key: 'privacy', label: 'Privacy', group: 'Privacy' },
  // Assets & Parties
  { key: 'assets', label: 'Assets', group: 'Assets & Parties' },
  { key: 'vendors', label: 'Vendors', group: 'Assets & Parties' },
  { key: 'employees', label: 'Employees', group: 'Assets & Parties' },
  // Assurance
  { key: 'gap', label: 'Assessments', group: 'Assurance' },
  { key: 'findings', label: 'Audits & Findings', group: 'Assurance' },
  // Resilience
  { key: 'bia', label: 'Business Impact', group: 'Resilience' },
  { key: 'bcdr', label: 'BC/DR', group: 'Resilience' },
  { key: 'incidents', label: 'Incidents', group: 'Resilience' },
  // Reporting
  { key: 'executive', label: 'Executive View', group: 'Reporting' },
  // Measurement programme (msr 2.7): the Metric Explorer today (admin only),
  // the improvement views in Phase 4.
  { key: 'improvement', label: 'Improvement', group: 'Reporting' },
  // System
  { key: 'integrations', label: 'Integrations', group: 'System' },
  { key: 'frameworks', label: 'Frameworks', group: 'System' },
];

// ── msr 4.10c (D28-A): the "Executive dashboard" permission ─────────────────
// A grant-only module key: absent means off for a manager or reader, and an
// admin always has it. The one rule is shared with the server
// (azure-functions/shared/executiveAccess.cjs), which decides; what the
// browser resolves here only drives what it shows. It is NOT the `executive`
// key above: every stored role and per-user permission set carries
// `executive: true` (new roles start with every module on), and that key never
// had an effect for a non-admin.
const EXEC_DASHBOARD_PERMISSION = Object.freeze({
  key: EXEC_DASHBOARD_MODULE,
  label: 'Executive dashboard',
  group: 'Reporting',
  grantOnly: true,
});

/**
 * The module rows a permission editor (Platform Roles, the user form) shows.
 * Switch off: SIDEBAR_MODULES exactly, as before 4.10c. Switch on: the inert
 * "Executive View" row is replaced by "Executive dashboard". The stored
 * `executive` value is never touched either way.
 * @param {string} publish 'on' | 'off' | 'loading' | 'unavailable'
 */
function permissionEditorModules(publish) {
  if (publish !== 'on') return SIDEBAR_MODULES;
  return SIDEBAR_MODULES.map((m) => (m.key === 'executive' ? EXEC_DASHBOARD_PERMISSION : m));
}

/**
 * Is a module on in a Platform Role's `modules` map? A grant-only key needs an explicit true.
 * @param {{ key: string, grantOnly?: boolean }} moduleEntry
 * @param {Record<string, boolean> | null | undefined} modules
 * @returns {boolean}
 */
function roleModuleOn(moduleEntry, modules) {
  const v = modules ? modules[moduleEntry.key] : undefined;
  return moduleEntry.grantOnly ? v === true : v !== false;
}

/** @type {Record<TenantRole, ModulePermissions>} */
const DEFAULT_MODULE_PERMISSIONS = {
  admin: {
    ...Object.fromEntries(SIDEBAR_MODULES.map((m) => [m.key, { access: true, mode: 'full' }])),
    [EXEC_DASHBOARD_MODULE]: { access: true, mode: 'readonly' },
  },
  manager: {
    ...Object.fromEntries(SIDEBAR_MODULES.map((m) => [m.key, { access: true, mode: 'full' }])),
    [EXEC_DASHBOARD_MODULE]: { access: false, mode: 'readonly' },
  },
  reader: {
    ...Object.fromEntries(SIDEBAR_MODULES.map((m) => [m.key, { access: true, mode: 'readonly' }])),
    [EXEC_DASHBOARD_MODULE]: { access: false, mode: 'readonly' },
  },
  prospect: { dashboard: { access: true, mode: 'readonly' } },
};

/**
 * Resolve effective module permissions for a session.
 *
 * @param {Session | null | undefined} session current user session (has .role, .permissions)
 * @param {SeasuiteRole | null} [seasuiteRole] resolved SeaSuite role object from
 *   state.seasuiteRoles (caller looks up the role by the user's seasuiteRoleId)
 * @returns {ModulePermissions}
 */
function getModulePermissions(session, seasuiteRole) {
  if (!session) return DEFAULT_MODULE_PERMISSIONS.reader;
  const role = session.role || 'reader';
  const defaults = DEFAULT_MODULE_PERMISSIONS[role] ?? DEFAULT_MODULE_PERMISSIONS.reader;
  if (role === 'admin') return defaults;
  // msr 4.10c: the Executive dashboard grant always comes from the shared rule.
  /** @type {ModuleAccess} */
  const execDashboard = {
    access: executiveDashboardAccess({
      role,
      seasuiteRole: seasuiteRole || null,
      permissions: session.permissions || null,
    }),
    mode: /** @type {'readonly'} */ ('readonly'),
  };

  // SeaSuite role overrides per-user permissions when assigned
  if (seasuiteRole?.modules) {
    const baseMode = role === 'reader' ? 'readonly' : 'full';
    /** @type {ModulePermissions} */
    const resolved = {};
    for (const m of SIDEBAR_MODULES) {
      resolved[m.key] = {
        access: seasuiteRole.modules[m.key] !== false,
        mode: baseMode,
      };
    }
    resolved[EXEC_DASHBOARD_MODULE] = execDashboard;
    return resolved;
  }

  // Fall back to per-user custom permissions
  const custom = session.permissions?.modules;
  if (!custom) return defaults;
  // Merge defaults for any modules missing from saved permissions
  return { ...defaults, ...custom, [EXEC_DASHBOARD_MODULE]: execDashboard };
}

/**
 * Look up the resolved SeaSuite role for a user.
 * Returns the role object from state.seasuiteRoles, or null.
 *
 * @param {string | null | undefined} userId
 * @param {TenantUser[] | null | undefined} users
 * @param {SeasuiteRole[] | null | undefined} seasuiteRoles
 * @returns {SeasuiteRole | null}
 */
function resolveSeasuiteRole(userId, users, seasuiteRoles) {
  if (!userId || !users || !seasuiteRoles?.length) return null;
  const user = users.find((u) => u.id === userId);
  if (!user?.seasuiteRoleId) return null;
  return seasuiteRoles.find((r) => r.id === user.seasuiteRoleId) || null;
}

export {
  SIDEBAR_MODULES,
  DEFAULT_MODULE_PERMISSIONS,
  EXEC_DASHBOARD_MODULE,
  EXEC_DASHBOARD_PERMISSION,
  permissionEditorModules,
  roleModuleOn,
  getModulePermissions,
  resolveSeasuiteRole,
};
